import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Spin } from 'antd';
// 使用 window.message 代替 antd 静态方法，避免 context 警告
import {
  LeftOutlined,
  RightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  FullscreenOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import useStyles from '../../styles/components/common/PDFViewer';
import * as pdfjs from 'pdfjs-dist';

// 设置worker路径 - 使用CDN路径，避免CORS问题
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const PDFViewer = ({
  url,
  title = 'PDF 预览',
  onClose,
  optimizeMode = false,
}) => {
  const { styles } = useStyles();
  const [pdfDocument, setPdfDocument] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  // 处理URL，只在开发环境使用代理
  const getProxyUrl = (originalUrl) => {
    if (!originalUrl) return originalUrl;

    console.log('import.meta.env', import.meta.env);
    // 只在开发环境且URL包含后端域名时，转换为通过代理访问
    if (
      import.meta.env.MODE === 'dev' &&
      originalUrl.includes('dev.service.syntrust.inside.h2os.cloud:7000')
    ) {
      // 提取路径部分：/v1/syntrust/attachments/...
      const urlObj = new URL(originalUrl);
      return urlObj.pathname + urlObj.search;
    }

    return originalUrl;
  };

  // 加载 PDF 文档
  const loadPDF = useCallback(async () => {
    if (!url) {
      setError('未提供PDF链接');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 获取代理URL
      const proxyUrl = getProxyUrl(url);

      // // 使用fetch获取PDF数据以避免CORS问题
      // const response = await fetch(proxyUrl);
      // if (!response.ok) {
      //   throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      // }

      // const pdfData = await response.arrayBuffer();

      // 使用获取到的数据加载PDF
      const loadingTask = pdfjs.getDocument({
        url: proxyUrl,
        // data: pdfData,
        // 禁用字体加载以提高性能
        disableFontFace: false,
      });

      const pdf = await loadingTask.promise;
      setPdfDocument(pdf);
      setTotalPages(pdf.numPages);
      setCurrentPage(1);
    } catch (err) {
      console.error('PDF 加载失败:', err);
      let errorMessage = 'PDF 文件加载失败';

      if (err.name === 'InvalidPDFException') {
        errorMessage = '无效的PDF文件格式';
      } else if (err.name === 'MissingPDFException') {
        errorMessage = 'PDF文件不存在或无法访问';
      } else if (err.name === 'UnexpectedResponseException') {
        errorMessage = '网络错误，请检查文件链接';
      }

      setError(errorMessage);
      window.message?.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [url]);

  // 初始加载 PDF 文档
  useEffect(() => {
    loadPDF();
  }, [loadPDF]);

  // 渲染当前页面
  useEffect(() => {
    const renderPage = async () => {
      if (!pdfDocument || !canvasRef.current) return;

      try {
        const page = await pdfDocument.getPage(currentPage);
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        // 清除画布
        context.clearRect(0, 0, canvas.width, canvas.height);

        // 计算视图，不强制修改旋转，让PDF保持原始方向
        const viewport = page.getViewport({ scale });

        // 设置canvas尺寸
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        // 渲染页面
        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };

        await page.render(renderContext).promise;
        reloadCurrentPage();
      } catch (err) {
        console.error('页面渲染失败:', err);
        window.message?.error('页面渲染失败');
      }
    };

    renderPage();
  }, [pdfDocument, currentPage, scale]);

  // 页面导航
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // 缩放控制
  const zoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 3.0));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  };

  const resetZoom = () => {
    setScale(1.0);
  };

  // 全屏预览
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  // 重新加载
  const reload = () => {
    setCurrentPage(1);
    setScale(1.0);
    setError(null);
    setPdfDocument(null);
    // 直接调用加载函数
    loadPDF();
  };

  // 重新渲染当前页面
  const reloadCurrentPage = async () => {
    if (!pdfDocument || !canvasRef.current) return;

    try {
      const page = await pdfDocument.getPage(currentPage);
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      // 清除画布
      context.clearRect(0, 0, canvas.width, canvas.height);

      // 计算视图
      const viewport = page.getViewport({ scale });

      // 设置canvas尺寸
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      // 渲染页面
      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      await page.render(renderContext).promise;
    } catch (err) {
      console.error('页面重新渲染失败:', err);
      window.message?.error('页面重新渲染失败');
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
        <div>正在加载 PDF 文件...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorTitle}>加载失败</div>
        <div className={styles.errorMessage}>{error}</div>
        <div className={styles.errorActions}>
          <button
            type="primary"
            onClick={reload}
            icon={<ReloadOutlined />}
            className={`px-4 py-2 rounded-full ${
              optimizeMode
                ? 'bg-[var(--color-assist-7)] hover:bg-[#e09b0c] border-[var(--color-assist-7)] hover:border-[#e09b0c]'
                : 'bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] border-[var(--color-primary)] hover:border-[var(--color-primary-hover)] text-white'
            }`}
            style={
              optimizeMode
                ? {
                    backgroundColor: 'var(--color-assist-7)',
                    borderColor: 'var(--color-assist-7)',
                  }
                : {}
            }
          >
            重新加载
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={styles.container}>
      {/* 工具栏 */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarSection}>
          <Button
            icon={<LeftOutlined />}
            disabled={currentPage <= 1}
            onClick={prevPage}
            size="small"
          />
          <span className={styles.pageInfo}>
            {currentPage} / {totalPages}
          </span>
          <Button
            icon={<RightOutlined />}
            disabled={currentPage >= totalPages}
            onClick={nextPage}
            size="small"
          />
        </div>

        <div className={styles.toolbarSection}>
          <Button
            icon={<ZoomOutOutlined />}
            onClick={zoomOut}
            disabled={scale <= 0.5}
            size="small"
          />
          <span className={styles.zoomInfo}>{Math.round(scale * 100)}%</span>
          <Button
            icon={<ZoomInOutlined />}
            onClick={zoomIn}
            disabled={scale >= 3.0}
            size="small"
          />
          <Button onClick={resetZoom} size="small">
            重置
          </Button>
        </div>

        <div className={styles.toolbarSection}>
          <Button
            icon={<ReloadOutlined />}
            onClick={reload}
            size="small"
            title="重新加载PDF文件"
            className={`${
              optimizeMode
                ? 'bg-[var(--color-assist-7)] hover:bg-[#e09b0c] border-[var(--color-assist-7)] hover:border-[#e09b0c]'
                : ''
            }`}
            style={
              optimizeMode
                ? {
                    backgroundColor: 'var(--color-assist-7)',
                    borderColor: 'var(--color-assist-7)',
                  }
                : {}
            }
          />
          <Button
            icon={<FullscreenOutlined />}
            onClick={toggleFullscreen}
            size="small"
            title="全屏显示"
          />
        </div>
      </div>

      {/* PDF 内容区域 */}
      <div className={styles.contentArea}>
        <div className={styles.canvasContainer}>
          <canvas ref={canvasRef} className={styles.canvas} />
        </div>
      </div>

      {/* 页面信息 */}
      <div className={styles.footer}>
        {title} - 第 {currentPage} 页，共 {totalPages} 页
      </div>
    </div>
  );
};

export default PDFViewer;
